import {useEffect, useMemo, useRef, useState} from 'react';
import Moment from 'moment';

import {
	OldTimeRegistrationTargetSuggestions,
	TimeRegistrationTargetSuggestions,
} from '../../../time-registration-target-suggestions/TimeRegistrationTargetSuggestionsFetch';
import {TimeRegEdge, TimeRegEntity, TimeRegistrationsForAWeek} from '../timesheet-table-util/TimeRegUtil';

import {
	addTimeRegsToMovedSuggestions,
	getEntityFromSuggestion,
	getHasTimeRegistrationFromSuggestion,
	getTimeRegsNotMatchedToSuggestionsInDayView,
	getTimeRegsWithoutMovedSuggestions,
} from '../timesheet-table-util/TimeRegSuggestionUtil';

type useSuggestionsProps = {
	suggestions: TimeRegistrationTargetSuggestions | OldTimeRegistrationTargetSuggestions | undefined;
	timeRegistrations: TimeRegEdge[];
	currentViewingDate: Moment.Moment;
	isWeekView: boolean;
};

type useSuggestionsOutput = {
	timeRegistrationsWithoutMovedSuggestions: TimeRegEdge[];
	suggestionEntitiesWithTimeRegs: Map<TimeRegEntity, TimeRegistrationsForAWeek>;
};

function getSuggestionEntititesWithAndWithoutTimeRegistrations(
	suggestionsAtLoad: TimeRegistrationTargetSuggestions | OldTimeRegistrationTargetSuggestions,
	timeRegistrationsAtLoad: TimeRegEdge[] | undefined
) {
	const suggestionEntities = new Map<TimeRegEntity, TimeRegistrationsForAWeek>();
	const suggestionsWithoutTimeRegs: string[] = [];

	suggestionsAtLoad?.forEach(suggestion => {
		const suggestionEntity = getEntityFromSuggestion(suggestion);

		const suggestionHasTimeRegistered = getHasTimeRegistrationFromSuggestion(suggestionEntity, timeRegistrationsAtLoad);

		if (!suggestionHasTimeRegistered) {
			suggestionEntities.set(suggestionEntity, [[], [], [], [], [], [], []]);
			suggestionsWithoutTimeRegs.push(suggestionEntity.id);
		}
	});
	return {suggestionEntities, suggestionsWithoutTimeRegs};
}

export const useSuggestions = ({
	suggestions,
	timeRegistrations,
	currentViewingDate,
	isWeekView,
}: useSuggestionsProps): useSuggestionsOutput => {
	const suggestionsNotHavingTimeRegsAtLoad = useRef<string[]>([]);
	const currentTimeRegistrationsRef = useRef<TimeRegEdge[] | undefined>(undefined);
	const suggestionsAtLoad = useRef<TimeRegistrationTargetSuggestions | OldTimeRegistrationTargetSuggestions>([]);
	const [suggestionEntities, setSuggestionEntities] = useState(new Map<TimeRegEntity, TimeRegistrationsForAWeek>());
	const isDayView = !isWeekView;

	function refreshSuggestionEntitiesWhenSuggestionsChange() {
		const {suggestionEntities, suggestionsWithoutTimeRegs} = getSuggestionEntititesWithAndWithoutTimeRegistrations(
			suggestionsAtLoad.current,
			currentTimeRegistrationsRef.current
		);

		suggestionsNotHavingTimeRegsAtLoad.current = suggestionsWithoutTimeRegs;
		setSuggestionEntities(suggestionEntities);
	}

	/* Clear refs when currentViewingDate or isWeekView changes */
	useEffect(() => {
		suggestionsNotHavingTimeRegsAtLoad.current = [];
		currentTimeRegistrationsRef.current = undefined;
		suggestionsAtLoad.current = [];
	}, [currentViewingDate, isWeekView]);

	/* Handle changes to suggestions */
	useEffect(() => {
		/* When suggestions are ready, refresh the suggestion entities */
		refreshSuggestionEntitiesWhenSuggestionsChange();
	}, [suggestionsAtLoad.current]);

	useEffect(() => {
		/* When suggestions are loaded for the first time in the session, persist them */
		if (suggestionsAtLoad.current?.length === 0 && suggestions) {
			suggestionsAtLoad.current = [...suggestions];
		}
	}, [suggestions]);

	/* Handle changes to time registrations */
	useEffect(() => {
		currentTimeRegistrationsRef.current = timeRegistrations;

		/*
		 * If a time reg has been created/deleted we need to refresh the suggestions
		 * */
		refreshSuggestionEntitiesWhenSuggestionsChange();
	}, [timeRegistrations]);

	const suggestionEntitiesWithTimeRegs = useMemo(() => {
		return addTimeRegsToMovedSuggestions(
			suggestionEntities,
			timeRegistrations,
			suggestionsNotHavingTimeRegsAtLoad.current,
			isDayView
		);
	}, [suggestionEntities, timeRegistrations, suggestionsNotHavingTimeRegsAtLoad]);

	/* based on the available suggestions and time registrations,
	 * calculate the return values:
	 * 	- time regs excluding any suggestions that "moved" to become time regs
	 *  - suggestions with time regs added when applicable
	 * */
	const timeRegsNotMatchedToSuggestions: string[] = useMemo(
		() =>
			isDayView
				? getTimeRegsNotMatchedToSuggestionsInDayView(
						suggestionEntities,
						timeRegistrations,
						suggestionsNotHavingTimeRegsAtLoad.current
				  )
				: [],
		[isDayView, suggestionEntities, timeRegistrations, suggestionsNotHavingTimeRegsAtLoad]
	);

	const timeRegistrationsWithoutMovedSuggestions = useMemo(
		() =>
			getTimeRegsWithoutMovedSuggestions(
				timeRegistrations,
				suggestionsNotHavingTimeRegsAtLoad.current,
				timeRegsNotMatchedToSuggestions
			),
		[timeRegistrations, suggestionsNotHavingTimeRegsAtLoad.current, timeRegsNotMatchedToSuggestions]
	);

	return {
		timeRegistrationsWithoutMovedSuggestions: timeRegistrationsWithoutMovedSuggestions,
		suggestionEntitiesWithTimeRegs: suggestionEntitiesWithTimeRegs,
	};
};
